import { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Set up axios interceptor to add auth token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Revenue metrics hook
export const useRevenueMetrics = (timeRange = 'week') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/analytics/revenue`, {
          params: { timeRange }
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching revenue metrics:', err);
        setError(err.response?.data?.error || 'Failed to fetch revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [timeRange]);

  return { data, loading, error };
};

// Customer metrics hook
export const useCustomerMetrics = (timeRange = 'week') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/analytics/customers`, {
          params: { timeRange }
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching customer metrics:', err);
        setError(err.response?.data?.error || 'Failed to fetch customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [timeRange]);

  return { data, loading, error };
};

// Marketing metrics hook
export const useMarketingMetrics = (timeRange = 'week') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/analytics/marketing`, {
          params: { timeRange }
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching marketing metrics:', err);
        setError(err.response?.data?.error || 'Failed to fetch marketing data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [timeRange]);

  return { data, loading, error };
};

// Product metrics hook
export const useProductMetrics = (timeRange = 'week') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/analytics/products`, {
          params: { timeRange }
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching product metrics:', err);
        setError(err.response?.data?.error || 'Failed to fetch product data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [timeRange]);

  return { data, loading, error };
};

// Connector status hook
export const useConnectorStatus = (brandId = 1) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE}/api/analytics/connectors/status`, {
          params: { brandId }
        });
        setData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching connector status:', err);
        setError(err.response?.data?.error || 'Failed to fetch connector status');
      } finally {
        setLoading(false);
      }
    };

    if (brandId) {
      fetchData();
    }
    
    // Refresh more frequently for connector status
    const interval = setInterval(() => {
      if (brandId) {
        fetchData();
      }
    }, 60 * 1000); // Every minute
    
    return () => clearInterval(interval);
  }, [brandId]);

  return { data, loading, error };
};

// Combined dashboard data hook
export const useDashboardData = (timeRange = 'week', brandId = 1) => {
  const revenue = useRevenueMetrics(timeRange);
  const customers = useCustomerMetrics(timeRange);
  const marketing = useMarketingMetrics(timeRange);
  const products = useProductMetrics(timeRange);
  const connectors = useConnectorStatus(brandId);

  const isLoading = revenue.loading || customers.loading || marketing.loading || products.loading;
  const hasError = revenue.error || customers.error || marketing.error || products.error;

  return {
    revenue: revenue.data,
    customers: customers.data,
    marketing: marketing.data,
    products: products.data,
    connectors: connectors.data,
    isLoading,
    hasError,
    errors: {
      revenue: revenue.error,
      customers: customers.error,
      marketing: marketing.error,
      products: products.error,
      connectors: connectors.error
    }
  };
};

// Cache management
export const clearAnalyticsCache = async () => {
  try {
    await axios.post(`${API_BASE}/api/analytics/cache/clear`);
    return { success: true };
  } catch (error) {
    console.error('Error clearing cache:', error);
    return { success: false, error: error.response?.data?.error || 'Failed to clear cache' };
  }
};
